import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { CalendarIcon, Mail } from "lucide-react";

const FUTURE_IMG = "https://static.prod-images.emergentagent.com/jobs/501ff75c-3ac8-43b3-9bce-7aac7ef89b3b/images/b333f08b3e6e99b738414e5efb860b5f0373e40f1f4f24c0df00a17079538ab0.png";

export default function FutureSelf() {
  const [recipient, setRecipient] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [date, setDate] = useState();
  const [letters, setLetters] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/letters");
      setLetters(data);
    } catch {
      // ignore
    }
  };
  useEffect(() => { load(); }, []);

  const send = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Pick a future delivery date."); return; }
    setBusy(true);
    try {
      const deliver_at = new Date(date);
      deliver_at.setHours(9, 0, 0, 0);
      await api.post("/letters", {
        recipient_email: recipient,
        subject,
        body,
        deliver_at: deliver_at.toISOString(),
      });
      toast.success("Your letter is scheduled.");
      setRecipient(""); setSubject(""); setBody(""); setDate(undefined);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not schedule.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="page-future-self">
      <div className="lg:col-span-7">
        <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
          <span className="editorial-rule" />Email your future self
        </div>
        <h1 className="font-serif text-5xl text-mindora-ink leading-tight">
          Write a letter you'll<br /><em className="italic text-mindora-red">need later.</em>
        </h1>
        <p className="mt-4 text-mindora-muted leading-relaxed max-w-lg">
          Reminders to your future self that you survived this week. A promise. A confession.
          A truth you don't want to lose. We'll send it on the date you choose.
        </p>

        <form onSubmit={send} className="mt-10 space-y-6" data-testid="future-self-form">
          <div>
            <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Send to</Label>
            <Input
              type="email"
              required
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              data-testid="future-recipient-input"
              placeholder="your-email@somewhere.com"
              className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
            />
          </div>
          <div>
            <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Subject</Label>
            <Input
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              data-testid="future-subject-input"
              placeholder="A note for you, six months from now"
              className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
            />
          </div>
          <div>
            <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Letter</Label>
            <Textarea
              required
              rows={9}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="future-body-input"
              placeholder="Hey future me, today I…"
              className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif text-lg leading-relaxed"
            />
          </div>
          <div>
            <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Deliver on</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="future-date-trigger"
                  className="mt-2 w-full justify-start text-left bg-mindora-bg border-mindora-line hover:bg-mindora-panel"
                >
                  <CalendarIcon size={16} className="mr-2 text-mindora-red" />
                  {date ? format(date, "PPP") : <span className="text-mindora-muted">Pick a future date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-mindora-bg border-mindora-line" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(d) => d <= new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <Button type="submit" disabled={busy} data-testid="future-submit-button" className="btn-primary rounded-full px-8 py-3 h-auto text-sm tracking-wide">
            {busy ? "Sealing…" : "Schedule the letter"}
          </Button>
        </form>
      </div>

      <div className="lg:col-span-5">
        <div className="rounded-3xl overflow-hidden border border-mindora-line shadow-clay">
          <img src={FUTURE_IMG} alt="" className="w-full h-72 object-cover" />
        </div>

        <h3 className="font-serif text-2xl text-mindora-ink mt-10">Scheduled letters</h3>
        <div className="mt-5 space-y-4" data-testid="future-letters-list">
          {letters.length === 0 ? (
            <p className="text-sm text-mindora-muted italic">No letters yet. Write one — your future self will be glad you did.</p>
          ) : (
            letters.map((l) => (
              <div key={l.id} className="rounded-2xl border border-mindora-line bg-mindora-panel p-5" data-testid={`future-letter-${l.id}`}>
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-mindora-red">{l.status}</span>
                  <span className="text-xs text-mindora-muted">{format(new Date(l.deliver_at), "PP")}</span>
                </div>
                <div className="font-serif text-lg text-mindora-ink mt-1.5 leading-snug">{l.subject}</div>
                <div className="flex items-center gap-1.5 mt-2 text-xs text-mindora-muted">
                  <Mail size={12} /> {l.recipient_email}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
